import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Send, MessageSquare, Loader2 } from 'lucide-react'
import athleteApi from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/sonner'
import { useAcceptCampaignMutation, useRejectCampaignMutation } from '@/redux/collaborationsApi'

interface MessageUser {
  id: number | null
  name: string | null
  avatar: string | null
}

interface MessageItem {
  id: number
  collaborationId: number
  userId: number | null
  milestoneId: number | null
  messageType: 'user' | 'system' | 'review'
  body: string
  metadata: Record<string, unknown> | null
  createdAt: string
  user: MessageUser | null
  milestone: { id: number; name: string; sequenceOrder: number } | null
}

interface BrandInfo {
  name: string
  logo: string | null
}

interface LicensingPlatform {
  platform: string
  territory: string
  usage_term_days: number
  fee: number
}

interface LicensingOffer {
  platforms: LicensingPlatform[]
  total_amount: number
  message?: string
}

interface DeliverableItem {
  id: string
  platform: string
  format: string
  notes: string | null
  order: number
}

interface CollaborationDetail {
  id: string
  status: string
  paymentType: 'fixed' | 'custom'
  paymentAmount: number | null
  customPaymentTerms: string | null
  contentDueDate: string | null
  bidReceivedAt?: string | null
  title: string | null
  platform: string | null
  startDate: string | null
  endDate: string | null
  brief: string | null
  brand: BrandInfo | null
  structuredDeliverables?: DeliverableItem[] | null
  offerDetails?: {
    message?: string
    licensing_offer?: LicensingOffer
  } | null
}

const OfferDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [acceptCampaign] = useAcceptCampaignMutation()
  const [rejectCampaign] = useRejectCampaignMutation()

  const [detail, setDetail] = useState<CollaborationDetail | null>(null)
  const [messages, setMessages] = useState<MessageItem[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [messageBody, setMessageBody] = useState('')

  const loadData = async () => {
    if (!id) return
    setLoading(true)
    try {
      const [collabRes, messagesRes] = await Promise.all([
        athleteApi.get(`/athlete/campaigns/${id}`),
        athleteApi.get(`/athlete/campaigns/${id}/messages`),
      ])
      setDetail(collabRes.data)
      setMessages(messagesRes.data.messages || [])
    } catch (err) {
      console.error('Failed to load offer detail', err)
      toast.error('Error', { description: 'Unable to load offer.' })
    } finally {
      setLoading(false)
    }
  }

  const refreshMessages = async () => {
    if (!id) return
    try {
      const res = await athleteApi.get(`/athlete/campaigns/${id}/messages`)
      setMessages(res.data.messages || [])
    } catch (err) {
      console.error('Failed to refresh messages', err)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const statusBadge = useMemo(() => {
    if (!detail) return null
    const map: Record<string, { label: string; color: string }> = {
      offered: { label: 'Awaiting Your Response', color: 'bg-blue-500' },
      accepted: { label: 'Accepted', color: 'bg-green-500' },
      rejected: { label: 'Declined', color: 'bg-red-500' },
    }
    const entry = map[detail.status] || { label: detail.status, color: 'bg-gray-500' }
    return <Badge className={`${entry.color} text-white`}>{entry.label}</Badge>
  }, [detail])

  const formatDate = (value: string | null | undefined) => {
    if (!value) return 'TBD'
    // Parse YYYY-MM-DD string directly without timezone conversion
    const dateStr = typeof value === 'string' ? value.split('T')[0] : value
    const [year, month, day] = dateStr.split('-')
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString()
  }

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return 'TBD'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
  }

  const sendMessage = async () => {
    if (!id || !messageBody.trim()) {
      toast('Message required', { description: 'Please enter a message first.' })
      return
    }
    setSending(true)
    try {
      await athleteApi.post(`/athlete/campaigns/${id}/negotiate`, { message: messageBody.trim() })
      setMessageBody('')
      await refreshMessages()
      toast('Message sent')
    } catch (err) {
      console.error('Failed to send message', err)
      toast.error('Error', { description: 'Could not send message.' })
    } finally {
      setSending(false)
    }
  }

  const handleAccept = async () => {
    if (!id) return
    try {
      await acceptCampaign(id.toString()).unwrap()
      await loadData()
      toast('Offer accepted')
    } catch (err) {
      toast.error('Error', { description: 'Failed to accept offer' })
    }
  }

  const handleDecline = async () => {
    if (!id) return
    try {
      await rejectCampaign(id.toString()).unwrap()
      await loadData()
      toast('Offer declined')
    } catch (err) {
      toast.error('Error', { description: 'Failed to decline offer' })
    }
  }

  const renderMessages = () => {
    // Separate system events from real conversation messages
    const conversationMessages = messages.filter((m) => m.messageType !== 'system')
    const systemEvents = messages.filter((m) => m.messageType === 'system')

    // Sort conversation messages oldest-first
    const sorted = [...conversationMessages].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )

    // Pick the earliest system event as the "offer sent" marker
    const offerSentEvent = systemEvents.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )[0]

    return (
      <div className="space-y-1">
        {/* Offer sent divider */}
        {offerSentEvent && (
          <div className="flex items-center gap-3 py-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-gray-600 uppercase tracking-wider shrink-0">
              Offer sent · {new Date(offerSentEvent.createdAt).toLocaleDateString()}
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
        )}

        {sorted.length === 0 ? (
          <div className="text-base text-gray-500 flex items-center gap-2 py-2">
            <MessageSquare className="h-5 w-5" /> No messages yet. Start the conversation.
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {sorted.map((msg) => {
              const author = msg.user
              const displayName = author?.name || 'Unknown'
              const isBrandSide = !!author?.name
              return (
                <div key={msg.id} className="py-5 first:pt-2 last:pb-0">
                  <div className="mb-2">
                    <div
                      className={`text-base font-medium ${isBrandSide ? 'text-[#58F0F0]' : 'text-white'}`}
                    >
                      {displayName}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {new Date(msg.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <p className="text-base text-gray-100 whitespace-pre-wrap leading-7">
                    {msg.body}
                  </p>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  if (loading || !detail) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/collaborations')}
          className="mb-4 hover:bg-[#C0FD72] hover:text-black"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Collaborations
        </Button>
        <div className="flex items-center gap-2 text-white">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading offer...
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate('/collaborations')}
          className="hover:bg-[#C0FD72] hover:text-black"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Collaborations
        </Button>
        {statusBadge}
      </div>

      <Card className="bg-white/5 border-white/10 text-white">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-2xl">
            <span>{detail.title || 'Offer Details'}</span>
            <span className="text-base text-gray-300 font-normal">
              Platform:{' '}
              {Array.isArray(detail.platform)
                ? detail.platform.join(', ') || '—'
                : detail.platform || '—'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-base text-gray-200">
            <span>
              Payout:{' '}
              {detail.paymentType === 'custom'
                ? detail.customPaymentTerms || 'Custom terms (see below)'
                : detail.paymentAmount
                  ? formatCurrency(detail.paymentAmount)
                  : 'TBD'}
            </span>
            <span>Content Due: {formatDate(detail.contentDueDate)}</span>
            {detail.bidReceivedAt && (
              <span>Offer received: {formatDate(detail.bidReceivedAt)}</span>
            )}
          </div>
          {detail.paymentType === 'custom' && detail.customPaymentTerms && (
            <div className="text-base text-gray-100">
              <p className="text-lg font-semibold text-white mb-2">Payment Terms</p>
              <p className="bg-white/5 border border-white/10 rounded p-4 text-gray-100 whitespace-pre-wrap leading-relaxed">
                {detail.customPaymentTerms}
              </p>
            </div>
          )}
          {detail.offerDetails?.message && (
            <div className="text-base text-gray-100">
              <p className="text-lg font-semibold text-white mb-2">Marketer Note</p>
              <p className="bg-white/5 border border-white/10 rounded p-4 text-gray-100 whitespace-pre-wrap leading-relaxed">
                {detail.offerDetails.message}
              </p>
            </div>
          )}
          {detail.structuredDeliverables && detail.structuredDeliverables.length > 0 && (
            <div>
              <p className="text-lg font-semibold text-white mb-3">Deliverables</p>
              <div className="space-y-2">
                {detail.structuredDeliverables.map((d, i) => (
                  <div
                    key={d.id}
                    className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/3 px-4 py-3"
                  >
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5 w-4 shrink-0">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-base text-white font-medium">
                        {d.platform} <span className="text-gray-400 font-normal">{d.format}</span>
                      </p>
                      {d.notes && <p className="text-sm text-gray-400 mt-0.5">{d.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {detail.offerDetails?.licensing_offer && (
            <div className="text-base bg-[#58F0F0]/10 border border-[#58F0F0]/20 rounded-lg p-4">
              <p className="text-lg font-semibold text-[#58F0F0] mb-2">
                Licensing Rights Requested
              </p>
              <div className="grid grid-cols-2 gap-3 text-gray-200">
                <div>
                  <span className="text-gray-400">Platforms:</span>{' '}
                  {detail.offerDetails.licensing_offer.platforms
                    .map((p) => p.platform.charAt(0).toUpperCase() + p.platform.slice(1))
                    .join(', ')}
                </div>
                <div>
                  <span className="text-gray-400">Territory:</span>{' '}
                  {detail.offerDetails.licensing_offer.platforms[0]?.territory || 'Worldwide'}
                </div>
                <div>
                  <span className="text-gray-400">Term:</span>{' '}
                  {detail.offerDetails.licensing_offer.platforms[0]?.usage_term_days || 30} days
                </div>
                <div>
                  <span className="text-gray-400">Fee:</span>{' '}
                  <span className="text-[#C0FD72] font-medium">Included in offer</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-2xl">Conversation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderMessages()}
          <div className="space-y-2">
            <Textarea
              placeholder="Ask a question or suggest changes..."
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              rows={3}
              className="bg-black/30 text-white text-base placeholder:text-gray-400 border-white/10 focus-visible:ring-white/30"
            />
            <div className="flex justify-end">
              <Button
                variant="lime"
                onClick={sendMessage}
                disabled={sending}
                className="text-base"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Message
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-2xl">Next Steps</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-base text-gray-200">
            {detail.status === 'offered'
              ? (() => {
                  const termsSet =
                    detail.paymentType === 'custom'
                      ? !!detail.customPaymentTerms
                      : !!detail.paymentAmount
                  return termsSet
                    ? 'Terms are set — you can accept to lock this in, or continue negotiating above.'
                    : 'No payment terms have been agreed yet. Use the conversation above to discuss terms. Accept will be available once terms are set.'
                })()
              : detail.status === 'accepted'
                ? 'You have accepted this offer. Proceed to the next steps in the dashboard.'
                : 'This offer has been declined.'}
          </div>
          {detail.status === 'offered' && (
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-white/20 text-white"
                onClick={handleDecline}
              >
                Decline
              </Button>
              {(
                detail.paymentType === 'custom'
                  ? !!detail.customPaymentTerms
                  : !!detail.paymentAmount
              ) ? (
                <Button variant="lime" onClick={handleAccept}>
                  Accept Offer
                </Button>
              ) : (
                <Button disabled className="bg-[#C0FD72]/40 text-black/50 cursor-not-allowed">
                  Accept Offer
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default OfferDetail
